import { DashboardViewWidget } from 'src/app/models/dashboard-view-widget.model';
import { GridWidgetConfig } from 'src/app/models/dashboard';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { GRID_ENTITY_OPTIONS } from 'src/app/shared/dashboard/widget-picker-dialog/widget-picker-dialog.component';
import { getWidgetDefinition } from './dashboard-widget-registry';
import { EMPTY_WIDGET_TITLE_META, WidgetTitleMeta } from 'src/app/models/dashboard/widget-title.model';

const GRID_VIEW_TYPE_KEY = 'grid-view';
const GRID_TAIL_SEPARATOR = ' - ';
const DEFAULT_VIEW_NAME_KEY = 'Default View';
const GRID_TAIL_PATTERN = /^ - .+$/;
const SUFFIX_TAIL_PATTERN = /^ \d+$/;
const TRAILING_SUFFIX_NUMBER_PATTERN = / (\d+)$/;

const IS_CUSTOM_TITLE_KEY = 'isCustomTitle';
const TITLE_SUFFIX_INDEX_KEY = 'titleSuffixIndex';

export const WIDGET_TITLE_CONFIG_KEYS: readonly string[] = [IS_CUSTOM_TITLE_KEY, TITLE_SUFFIX_INDEX_KEY];

const LEGACY_DEFAULT_TITLE_ALIASES: Record<string, string> = {
    'Sample Pipeline Status': 'Sample Workflow Pipeline',
    'Exceedance Compliance Overview': 'Exceedance Compliance',
    'Survey Workflow Status': 'Survey Progress',
};

export interface ResolvedWidgetTitle {
    widgetTitle: string;
    titleMeta: WidgetTitleMeta;
}

function nonCustomResult(baseName: string, meta: Partial<WidgetTitleMeta> = {}): ResolvedWidgetTitle {
    return { widgetTitle: baseName, titleMeta: { ...EMPTY_WIDGET_TITLE_META, ...meta } };
}

function customResult(title: string): ResolvedWidgetTitle {
    return { widgetTitle: title, titleMeta: { ...EMPTY_WIDGET_TITLE_META, isCustom: true } };
}

function resolveBaseName(typeKey: string, configJson: Record<string, unknown>): string {
    if (typeKey === GRID_VIEW_TYPE_KEY) {
        const objectId = configJson[nameOf<GridWidgetConfig>(x => x.objectId)];
        const entity = typeof objectId === 'string' ? GRID_ENTITY_OPTIONS.find(e => e.objectId === objectId) : undefined;
        if (entity?.name) return entity.name;
    }
    return getWidgetDefinition(typeKey)?.name || typeKey;
}

function readSuffixTail(configJson: Record<string, unknown>): string {
    const suffixIndex = configJson[TITLE_SUFFIX_INDEX_KEY];
    return typeof suffixIndex === 'number' ? ` ${suffixIndex}` : '';
}

function buildLiveMeta(typeKey: string, configJson: Record<string, unknown>): Partial<WidgetTitleMeta> {
    const suffixTail = readSuffixTail(configJson);

    if (typeKey !== GRID_VIEW_TYPE_KEY) {
        return { tail: suffixTail };
    }

    const rawViewName = configJson[nameOf<GridWidgetConfig>(x => x.viewName)];
    return typeof rawViewName === 'string' && rawViewName
        ? { tailPrefix: GRID_TAIL_SEPARATOR, tail: `${rawViewName}${suffixTail}` }
        : { tailPrefix: GRID_TAIL_SEPARATOR, tailKey: DEFAULT_VIEW_NAME_KEY, tail: suffixTail };
}

function isRecognizedLegacyTail(typeKey: string, tail: string): boolean {
    return typeKey === GRID_VIEW_TYPE_KEY ? GRID_TAIL_PATTERN.test(tail) : SUFFIX_TAIL_PATTERN.test(tail);
}

function classifyLegacyTitle(title: string, typeKey: string, baseName: string): ResolvedWidgetTitle {
    if (LEGACY_DEFAULT_TITLE_ALIASES[title] === baseName || title === baseName) {
        return nonCustomResult(baseName);
    }

    if (baseName && title.startsWith(baseName)) {
        const tail = title.slice(baseName.length);
        if (isRecognizedLegacyTail(typeKey, tail)) {
            return nonCustomResult(baseName, { tail });
        }
    }

    return customResult(title);
}

export function resolveWidgetTitle(widget: DashboardViewWidget, typeKey: string): ResolvedWidgetTitle {
    const configJson = widget.configJson || {};
    const baseName = resolveBaseName(typeKey, configJson);

    if (isWidgetTitleCustom(configJson)) {
        return customResult(widget.title || '');
    }

    if (!widget.title) {
        return nonCustomResult(baseName, buildLiveMeta(typeKey, configJson));
    }

    return classifyLegacyTitle(widget.title, typeKey, baseName);
}

export function getTitleSuffixIndex(widget: DashboardViewWidget, typeKey: string): number | undefined {
    const storedIndex = (widget.configJson || {})[TITLE_SUFFIX_INDEX_KEY];
    if (typeof storedIndex === 'number') return storedIndex;
    if (typeKey === GRID_VIEW_TYPE_KEY) return undefined;

    const { titleMeta } = resolveWidgetTitle(widget, typeKey);
    if (titleMeta.isCustom) return undefined;

    const match = TRAILING_SUFFIX_NUMBER_PATTERN.exec(titleMeta.tail);
    return match ? parseInt(match[1], 10) : undefined;
}

export function withTitleSuffixIndex(
    configJson: Record<string, unknown>,
    suffixIndex: number | undefined
): Record<string, unknown> {
    return suffixIndex == null ? configJson : { ...configJson, [TITLE_SUFFIX_INDEX_KEY]: suffixIndex };
}

export function isWidgetTitleCustom(configJson: Record<string, unknown> | undefined): boolean {
    return configJson?.[IS_CUSTOM_TITLE_KEY] === true;
}

export function markCustomTitle(configJson: Record<string, unknown> | undefined, isCustom: boolean): Record<string, unknown> {
    const updated = { ...(configJson || {}) };
    if (isCustom) {
        updated[IS_CUSTOM_TITLE_KEY] = true;
    } else {
        delete updated[IS_CUSTOM_TITLE_KEY];
    }
    return updated;
}

export function preserveTitleConfig(
    previousConfig: Record<string, unknown> | undefined,
    nextConfig: Record<string, unknown>
): Record<string, unknown> {
    const merged = { ...nextConfig };
    WIDGET_TITLE_CONFIG_KEYS.forEach(key => {
        const previousValue = previousConfig?.[key];
        if (previousValue !== undefined) merged[key] = previousValue;
    });
    return merged;
}
